import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader2, Send, AlertCircle, X, ChevronRight, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { VoiceRecorder, getFileExtension } from '@/components/surveys/VoiceRecorder';
import { cn } from '@/lib/utils';
import type {
  SurveyTemplate,
  SurveyQuestion,
  SurveyRespondent,
  SurveyCampaign,
  SurveyInvitation
} from '@/types/database';

interface CampaignSurvey {
  template: SurveyTemplate;
  questions: SurveyQuestion[];
  invitation: SurveyInvitation;
}

export function CampaignResponse() {
  const { campaignId, respondentId } = useParams<{
    campaignId: string;
    respondentId: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<SurveyCampaign | null>(null);
  const [surveys, setSurveys] = useState<CampaignSurvey[]>([]);
  const [respondent, setRespondent] = useState<SurveyRespondent | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allComplete, setAllComplete] = useState(false);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [voiceResponses, setVoiceResponses] = useState<Record<string, Blob>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const loadCampaignData = useCallback(async () => {
    if (!campaignId || !respondentId) {
      setLoadError('Invalid campaign link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);

      const { data: campaignData, error: campaignError } = await supabase
        .from('survey_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (campaignError || !campaignData) {
        throw new Error('Campaign not found');
      }

      setCampaign(campaignData);

      const { data: invitations, error: invError } = await supabase
        .from('survey_invitations')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('respondent_id', respondentId)
        .in('status', ['sent', 'completed']);

      if (invError) throw invError;
      if (!invitations || invitations.length === 0) {
        throw new Error('No valid invitations found for this campaign');
      }

      const expired = invitations.every(
        inv => inv.status === 'sent' && inv.expires_at && new Date(inv.expires_at) < new Date()
      );
      if (expired) {
        throw new Error('This campaign link has expired');
      }

      const { data: campaignSurveys, error: csError } = await supabase
        .from('survey_campaign_surveys')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('order_index');

      if (csError) throw csError;
      if (!campaignSurveys || campaignSurveys.length === 0) {
        throw new Error('No surveys found in this campaign');
      }

      const surveyIds = campaignSurveys.map(cs => cs.survey_id);
      const { data: templates, error: tError } = await supabase
        .from('survey_templates')
        .select('*, questions:survey_questions(*)')
        .in('id', surveyIds);

      if (tError) throw tError;

      const templateMap = new Map(
        (templates || []).map(t => [t.id, t])
      );
      const invitationMap = new Map(
        invitations.map(inv => [inv.survey_id, inv])
      );

      const orderedSurveys: CampaignSurvey[] = [];
      for (const cs of campaignSurveys) {
        const tmpl = templateMap.get(cs.survey_id);
        const inv = invitationMap.get(cs.survey_id);
        if (tmpl && inv) {
          orderedSurveys.push({
            template: tmpl,
            questions: tmpl.questions || [],
            invitation: inv
          });
        }
      }

      if (orderedSurveys.length === 0) {
        throw new Error('No valid surveys found');
      }

      setSurveys(orderedSurveys);

      const { data: respData, error: respError } = await supabase
        .from('survey_respondents')
        .select('*')
        .eq('id', respondentId)
        .single();

      if (respError) throw respError;
      setRespondent(respData);

      const firstIncomplete = orderedSurveys.findIndex(
        s => s.invitation.status !== 'completed'
      );
      if (firstIncomplete === -1) {
        setAllComplete(true);
      } else {
        setCurrentIndex(firstIncomplete);
        initAnswers(orderedSurveys[firstIncomplete].questions);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [campaignId, respondentId]);

  useEffect(() => {
    loadCampaignData();
  }, [loadCampaignData]);

  const initAnswers = (questions: SurveyQuestion[]) => {
    const initial: Record<string, string> = {};
    questions.forEach(q => {
      initial[q.id] = '';
    });
    setAnswers(initial);
    setVoiceResponses({});
    setValidationAttempted(false);
    setValidationErrors({});
    setSubmitError(null);
  };

  const currentSurvey = surveys[currentIndex];
  const currentQuestions = currentSurvey?.questions || [];
  const completedCount = surveys.filter(s => s.invitation.status === 'completed').length;

  const handleVoiceResponse = (questionId: string, audioBlob: Blob) => {
    setVoiceResponses(prev => ({ ...prev, [questionId]: audioBlob }));
  };

  const validateAnswers = () => {
    const errors: Record<string, string> = {};
    currentQuestions.forEach(q => {
      const hasText = answers[q.id]?.trim();
      const hasVoice = !!voiceResponses[q.id];
      if (!hasText && !hasVoice) {
        errors[q.id] = 'This question requires an answer';
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentSurvey || !respondent) return;

    setSubmitting(true);
    setSubmitError(null);
    setValidationAttempted(true);

    try {
      if (!validateAnswers()) {
        setSubmitting(false);
        const firstErrorId = currentQuestions.find(q => {
          const hasText = answers[q.id]?.trim();
          const hasVoice = !!voiceResponses[q.id];
          return !hasText && !hasVoice;
        })?.id;
        if (firstErrorId) {
          document.getElementById(`question-${firstErrorId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
        return;
      }

      const responses = await Promise.all(
        currentQuestions.map(async question => {
          const response = {
            template_id: currentSurvey.template.id,
            organization_id: respondent.organization_id,
            respondent_id: respondentId!,
            phone_number: respondent.phone_number || '',
            question_id: question.id,
            answer: (answers[question.id] || '').trim(),
            voice_response_url: null as string | null
          };

          if (voiceResponses[question.id]) {
            const blob = voiceResponses[question.id];
            const ext = getFileExtension(blob.type);
            const filename = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
            const filePath = `${respondentId}/${filename}`;

            const { error: uploadError } = await supabase.storage
              .from('voice_responses')
              .upload(filePath, blob, { contentType: blob.type });

            if (uploadError) throw uploadError;

            const {
              data: { publicUrl }
            } = supabase.storage.from('voice_responses').getPublicUrl(filePath);

            response.voice_response_url = publicUrl;
          }

          return response;
        })
      );

      const { error: responsesError } = await supabase
        .from('survey_responses')
        .upsert(responses, { onConflict: 'respondent_id,question_id' });

      if (responsesError) throw new Error(responsesError.message);

      const { error: invitationError } = await supabase
        .from('survey_invitations')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', currentSurvey.invitation.id);

      if (invitationError) throw new Error(invitationError.message);

      const updatedSurveys = [...surveys];
      updatedSurveys[currentIndex] = {
        ...updatedSurveys[currentIndex],
        invitation: { ...updatedSurveys[currentIndex].invitation, status: 'completed' }
      };
      setSurveys(updatedSurveys);

      const nextIncomplete = updatedSurveys.findIndex(
        (s, i) => i > currentIndex && s.invitation.status !== 'completed'
      );

      if (nextIncomplete === -1) {
        setAllComplete(true);
      } else {
        setCurrentIndex(nextIncomplete);
        initAnswers(updatedSurveys[nextIncomplete].questions);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit survey');
      requestAnimationFrame(() => {
        document.getElementById('submit-error')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    const nextIncomplete = surveys.findIndex(
      (s, i) => i > currentIndex && s.invitation.status !== 'completed'
    );
    if (nextIncomplete === -1) {
      setAllComplete(true);
    } else {
      setCurrentIndex(nextIncomplete);
      initAnswers(surveys[nextIncomplete].questions);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const hasNextIncompleteSurvey = surveys.some(
    (s, i) => i > currentIndex && s.invitation.status !== 'completed'
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {loadError}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              If you believe this is an error, please contact your administrator.
            </p>
            <Button variant="outline" onClick={() => window.close()} className="w-full">
              Close Window
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (allComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Thank you for completing all surveys!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You completed {surveys.length} survey{surveys.length !== 1 ? 's' : ''} in the{' '}
              <span className="font-medium">{campaign?.name}</span> campaign. Your responses
              have been recorded. You can now close this window.
            </p>
            <Button variant="outline" onClick={() => window.close()} className="w-full">
              Close Window
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Campaign progress header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {campaign?.name}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Survey {currentIndex + 1} of {surveys.length}
            </span>
          </div>
          <div className="flex gap-1">
            {surveys.map((s, i) => (
              <div
                key={s.template.id}
                className={cn('h-2 flex-1 rounded-full', {
                  'bg-green-500 dark:bg-green-400': s.invitation.status === 'completed',
                  'bg-blue-600 dark:bg-blue-500': i === currentIndex && s.invitation.status !== 'completed',
                  'bg-gray-200 dark:bg-gray-700': i !== currentIndex && s.invitation.status !== 'completed'
                })}
              />
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {currentSurvey?.template.title}
            </h1>
            {currentSurvey?.template.description && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {currentSurvey.template.description}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {submitError && (
              <div
                id="submit-error"
                className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-start justify-between"
              >
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <p>{submitError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSubmitError(null)}
                  className="ml-4 flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {validationAttempted && Object.keys(validationErrors).length > 0 && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Please fix the following errors:</p>
                  <p className="mt-1">
                    All questions must be answered before submitting the survey.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {currentQuestions.map((question, index) => {
                const hasError = validationAttempted && validationErrors[question.id];
                return (
                  <div
                    key={question.id}
                    id={`question-${question.id}`}
                    className={cn(
                      'space-y-4 rounded-lg p-4',
                      hasError &&
                        'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {hasError && (
                        <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-1" />
                      )}
                      <p
                        className={cn(
                          'text-lg font-medium',
                          hasError
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-gray-900 dark:text-white'
                        )}
                      >
                        {index + 1}. {question.text}
                      </p>
                    </div>

                    {question.type === 'multiple-choice' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => (
                          <label
                            key={optionIndex}
                            className={cn(
                              'flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer',
                              hasError
                                ? 'border-red-200 dark:border-red-800 hover:bg-red-50/50 dark:hover:bg-red-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            )}
                          >
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={option}
                              checked={answers[question.id] === option}
                              onChange={e =>
                                setAnswers({ ...answers, [question.id]: e.target.value })
                              }
                              className={cn(
                                'h-4 w-4 border focus:ring-2',
                                hasError
                                  ? 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 focus:ring-red-500'
                                  : 'text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                              )}
                            />
                            <span className="text-gray-900 dark:text-white">{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : question.type === 'multi-select' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => {
                          const selected: string[] = (() => {
                            const val = answers[question.id];
                            if (!val) return [];
                            try { return JSON.parse(val); } catch { return []; }
                          })();
                          const isChecked = selected.includes(option);
                          return (
                            <label
                              key={optionIndex}
                              className={cn(
                                'flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer',
                                hasError
                                  ? 'border-red-200 dark:border-red-800 hover:bg-red-50/50 dark:hover:bg-red-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  const next = isChecked
                                    ? selected.filter(s => s !== option)
                                    : [...selected, option];
                                  setAnswers({
                                    ...answers,
                                    [question.id]: JSON.stringify(next)
                                  });
                                }}
                                className={cn(
                                  'h-4 w-4 rounded border focus:ring-2',
                                  hasError
                                    ? 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 focus:ring-red-500'
                                    : 'text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                                )}
                              />
                              <span className="text-gray-900 dark:text-white">{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={e =>
                            setAnswers({ ...answers, [question.id]: e.target.value })
                          }
                          rows={4}
                          className={cn(
                            'w-full rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2',
                            hasError
                              ? 'border-red-200 dark:border-red-800 focus:ring-red-500'
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          )}
                          placeholder="Enter your answer here..."
                        />
                        <VoiceRecorder
                          onSave={async audioBlob => {
                            handleVoiceResponse(question.id, audioBlob);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              {hasNextIncompleteSurvey && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={handleSkip}
                >
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip Survey
                </Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : currentIndex < surveys.length - 1 ? (
                  <>
                    Submit &amp; Next Survey
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Final Survey
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
